import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { sampleLineSegments } from '../pointCloudUtils';
import { GRID_DIM, GRID_IMPORT, GRID_EXPORT, WIRE_DIM } from '../twinColors';
const WIRE_LENGTH = 18;
const WIRE_YS = [7.5, 8.0, 8.5];
const WIRE_START_X = -0.6 - WIRE_LENGTH;
const WIRE_END_X = -0.6;

/**
 * GridConnection - Point cloud transmission tower with
 * 3 power lines and directional pulse dots.
 */
export default function GridConnection({ state, position, onClick }) {
    const dotsRef = useRef();

    const gridPower = state?.power_kw ?? 0;
    const isImporting = gridPower > 0;
    const isExporting = gridPower < 0;
    const isActive = gridPower !== 0;

    // Build tower from line segments, then sample points
    const towerPoints = useMemo(() => {
        const positions = [];

        const baseCorners = [
            [-1.5, 0, -0.5], [1.5, 0, -0.5],
            [1.5, 0, 0.5], [-1.5, 0, 0.5],
        ];
        const topCorners = [
            [-0.6, 8, -0.2], [0.6, 8, -0.2],
            [0.6, 8, 0.2], [-0.6, 8, 0.2],
        ];

        // 4 legs
        for (let i = 0; i < 4; i++) {
            positions.push(...baseCorners[i], ...topCorners[i]);
        }

        // Cross-brace rings at Y=3 and Y=6
        for (const y of [3, 6]) {
            const t = y / 8;
            const corners = baseCorners.map((bc, ci) => [
                bc[0] + (topCorners[ci][0] - bc[0]) * t,
                y,
                bc[2] + (topCorners[ci][2] - bc[2]) * t,
            ]);
            for (let i = 0; i < 4; i++) {
                positions.push(...corners[i], ...corners[(i + 1) % 4]);
            }
        }

        // Top platform
        for (let i = 0; i < 4; i++) {
            positions.push(...topCorners[i], ...topCorners[(i + 1) % 4]);
        }

        // Wire attachment bars
        for (const y of WIRE_YS) {
            positions.push(-0.6, y, 0, 0.6, y, 0);
        }

        return sampleLineSegments(new Float32Array(positions), 350, 0.06);
    }, []);

    // Wire points as particles
    const wirePoints = useMemo(() => {
        const pts = [];
        for (const y of WIRE_YS) {
            for (let i = 0; i < 40; i++) {
                const t = i / 39;
                pts.push(
                    WIRE_START_X + t * WIRE_LENGTH,
                    y + (Math.random() - 0.5) * 0.03,
                    (Math.random() - 0.5) * 0.03
                );
            }
        }
        return new Float32Array(pts);
    }, []);

    // Pulse dots on wires
    const dotBuffer = useMemo(() => {
        const buf = new Float32Array(9);
        for (let i = 0; i < 3; i++) {
            buf[i * 3] = WIRE_START_X + WIRE_LENGTH / 2;
            buf[i * 3 + 1] = WIRE_YS[i];
            buf[i * 3 + 2] = 0;
        }
        return buf;
    }, []);

    const dotColor = useMemo(() => {
        if (isImporting) return GRID_IMPORT;
        if (isExporting) return GRID_EXPORT;
        return GRID_DIM;
    }, [isImporting, isExporting]);

    useFrame(() => {
        if (!dotsRef.current) return;

        if (!isActive) {
            dotsRef.current.visible = false;
            return;
        }

        dotsRef.current.visible = true;
        const posArr = dotsRef.current.geometry.attributes.position.array;
        const speed = 0.1 + Math.abs(gridPower) / 500 * 0.3;
        const dir = isImporting ? 1 : -1;

        for (let i = 0; i < 3; i++) {
            posArr[i * 3] += speed * dir;
            if (dir > 0 && posArr[i * 3] > WIRE_END_X) posArr[i * 3] = WIRE_START_X;
            else if (dir < 0 && posArr[i * 3] < WIRE_START_X) posArr[i * 3] = WIRE_END_X;
            posArr[i * 3 + 1] = WIRE_YS[i];
            posArr[i * 3 + 2] = 0;
        }

        dotsRef.current.geometry.attributes.position.needsUpdate = true;
    });

    return (
        <group position={position} onClick={onClick}>
            {/* Tower particle cloud */}
            <points>
                <bufferGeometry>
                    <bufferAttribute
                        attach="attributes-position"
                        count={towerPoints.length / 3}
                        array={towerPoints}
                        itemSize={3}
                    />
                </bufferGeometry>
                <pointsMaterial
                    color={GRID_DIM}
                    size={0.1}
                    transparent
                    opacity={0.5}
                    depthWrite={false}
                    sizeAttenuation
                    blending={THREE.AdditiveBlending}
                />
            </points>

            {/* Wire particles */}
            <points>
                <bufferGeometry>
                    <bufferAttribute
                        attach="attributes-position"
                        count={wirePoints.length / 3}
                        array={wirePoints}
                        itemSize={3}
                    />
                </bufferGeometry>
                <pointsMaterial
                    color={WIRE_DIM}
                    size={0.06}
                    transparent
                    opacity={0.3}
                    depthWrite={false}
                    sizeAttenuation
                    blending={THREE.AdditiveBlending}
                />
            </points>

            {/* Directional pulse dots */}
            <points ref={dotsRef} visible={false}>
                <bufferGeometry>
                    <bufferAttribute
                        attach="attributes-position"
                        count={3}
                        array={dotBuffer}
                        itemSize={3}
                    />
                </bufferGeometry>
                <pointsMaterial
                    color={dotColor}
                    size={0.6}
                    transparent
                    opacity={0.9}
                    depthWrite={false}
                    sizeAttenuation
                    blending={THREE.AdditiveBlending}
                />
            </points>
        </group>
    );
}
